<template>
  <div class="h-screen flex w-full bg-img">
    <div class="vx-col sm:w-3/5 md:w-3/5 lg:w-3/4 xl:w-3/5 mx-auto self-center">
      <vx-card>
        <div slot="no-body" class="full-page-bg-color">
          <div class="vx-row">
            <div class="vx-col hidden sm:hidden md:hidden lg:block lg:w-1/2 mx-auto self-center">
              <img src="https://xylo.codesinn.com/logo-xylo-impact.svg" alt="XYLO" class="mx-auto" width="250">
            </div>
            <div class="vx-col sm:w-full md:w-full lg:w-1/2 mx-auto self-center d-theme-dark-bg">
              <div class="p-8">
                <div class="vx-card__title mb-8">
                  <h4 class="mb-4">Reset your password</h4>
                  <p v-if="token">Choose a new password. The link is valid for 30 minutes.</p>
                  <vs-alert v-else color="danger" class="mt-2" :active="true">
                    Missing or invalid reset token. Open the link from your email, or request a new one.
                  </vs-alert>
                </div>

                <template v-if="token && !done">
                  <vs-input
                    type="password"
                    label-placeholder="New password"
                    v-model="newPassword"
                    icon-pack="feather" icon="icon-shield" icon-no-border
                    :maxlength="64"
                    class="w-full mb-6"
                  />
                  <vs-input
                    type="password"
                    label-placeholder="Confirm new password"
                    v-model="confirmPassword"
                    icon-pack="feather" icon="icon-shield" icon-no-border
                    :maxlength="64"
                    class="w-full mb-6"
                    @keyup.enter="submit"
                  />

                  <div class="mt-2" v-if="newPassword.length > 0">
                    <div class="strength-bar"><div :class="['strength-fill', strengthClass]" :style="{width: ((zxScore + 1) * 20) + '%'}"></div></div>
                    <p class="text-xs mt-1" :class="strengthTextClass">{{ strengthLabel }}</p>
                    <p class="text-xs mt-1" v-if="zxFeedback">{{ zxFeedback }}</p>

                    <ul class="pwd-checklist text-xs mt-2">
                      <li :class="reqLength ? 'ok' : 'ko'">
                        <span class="mark">{{ reqLength ? '✓' : '○' }}</span> At least 12 characters ({{ newPassword.length }}/12)
                      </li>
                      <li :class="reqStrength ? 'ok' : 'ko'">
                        <span class="mark">{{ reqStrength ? '✓' : '○' }}</span> Strength: Strong or better (current: {{ strengthLabel }})
                      </li>
                      <li :class="reqMatch ? 'ok' : 'ko'">
                        <span class="mark">{{ reqMatch ? '✓' : '○' }}</span> New password and confirmation match
                      </li>
                    </ul>
                  </div>

                  <vs-alert v-if="error" color="danger" class="mt-4">{{ error }}</vs-alert>

                  <div class="flex flex-wrap justify-between flex-col-reverse sm:flex-row mt-4">
                    <vs-button type="border" to="/pages/login" class="w-full sm:w-auto mb-4 sm:mb-0">Back to login</vs-button>
                    <vs-button :disabled="!canSubmit" @click="submit" class="w-full sm:w-auto">Reset password</vs-button>
                  </div>
                </template>

                <template v-if="done">
                  <vs-alert color="success" :active="true" class="mb-4">
                    Your password has been reset. All trusted-device sessions on your account were revoked. You can now sign in with your new password.
                  </vs-alert>
                  <vs-button to="/pages/login" class="w-full sm:w-auto">Go to login</vs-button>
                </template>

                <template v-if="!token">
                  <vs-button to="/pages/forgot-password" type="border" class="w-full sm:w-auto mt-4">Request a new link</vs-button>
                </template>
              </div>
            </div>
          </div>
        </div>
      </vx-card>
    </div>
  </div>
</template>

<script>
import axios from '@/axios.js'
import zxcvbn from 'zxcvbn'

export default {
  data () {
    return {
      newPassword: '',
      confirmPassword: '',
      error: null,
      done: false,
      submitting: false
    }
  },
  computed: {
    token () {
      return this.$route.query.token || ''
    },
    zx () { return this.newPassword ? zxcvbn(this.newPassword) : null },
    zxScore () { return this.zx ? this.zx.score : 0 },
    zxFeedback () {
      if (!this.zx) return null
      const fb = this.zx.feedback || {}
      return fb.warning || (fb.suggestions || []).join(' ')
    },
    strengthLabel () { return ['Very weak', 'Weak', 'Fair', 'Strong', 'Very strong'][this.zxScore] },
    strengthClass () { return ['s-vweak', 's-weak', 's-fair', 's-strong', 's-vstrong'][this.zxScore] },
    strengthTextClass () { return this.zxScore >= 3 ? 'text-success' : 'text-warning' },
    reqLength   () { return this.newPassword.length >= 12 },
    reqStrength () { return this.zxScore >= 3 },
    reqMatch    () { return this.newPassword.length > 0 && this.newPassword === this.confirmPassword },
    canSubmit () {
      return !this.submitting && this.reqLength && this.reqStrength && this.reqMatch
    }
  },
  methods: {
    async submit () {
      this.error = null
      if (!this.canSubmit) return
      this.submitting = true
      this.$vs.loading()
      try {
        await axios.post('/auth/reset-password', {
          token: this.token,
          newPassword: this.newPassword
        })
        this.done = true
      } catch (e) {
        this.error = (e.response && e.response.data && e.response.data.message) || 'Could not reset the password. Please request a new link.'
      } finally {
        this.$vs.loading.close()
        this.submitting = false
      }
    }
  }
}
</script>

<style scoped>
.strength-bar {
  height: 4px;
  background: #eee;
  border-radius: 2px;
  overflow: hidden;
}
.strength-fill {
  height: 100%;
  transition: width 200ms, background-color 200ms;
}
.s-vweak   { background: #d33; }
.s-weak    { background: #e66; }
.s-fair    { background: #ea0; }
.s-strong  { background: #6c0; }
.s-vstrong { background: #2a8; }
.pwd-checklist {
  list-style: none;
  padding: 0;
  margin: 0;
}
.pwd-checklist li {
  display: flex;
  align-items: center;
  line-height: 1.6;
}
.pwd-checklist .mark {
  display: inline-block;
  width: 14px;
  text-align: center;
  margin-right: 6px;
  font-weight: bold;
}
.pwd-checklist li.ok { color: #2a8; }
.pwd-checklist li.ko { color: #888; }
</style>
